import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@saas-kit/ui';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

/** Logo picker for the branding tab — direct upload with a live preview. */
export function LogoUploader({
  currentUrl,
  uploading,
  onPick,
  onClear,
}: {
  currentUrl: string | null;
  uploading: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">
        {t('branding.logo')}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={t('branding.logo')}
            className="max-h-24 max-w-[12rem] rounded border border-border bg-white object-contain p-2"
            data-testid="branding-logo-preview"
          />
        ) : (
          <div
            className="flex h-24 w-48 items-center justify-center rounded border border-dashed border-border text-xs text-muted-foreground"
            data-testid="branding-logo-empty"
          >
            {t('branding.logo_empty')}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          data-testid="branding-logo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Spinner size="sm" /> : null}
          {t(currentUrl ? 'branding.logo_replace' : 'branding.logo_upload')}
        </Button>
        {currentUrl ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onClear}
            data-testid="branding-logo-clear"
          >
            {t('branding.logo_clear')}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{t('branding.logo_hint')}</p>
    </div>
  );
}
